import type {
  ApiResponse,
  DeviceCommand,
  DeviceCommandType,
  DeviceSummary,
  HomeSection,
  HomeTarget,
  ParentSearchResult,
  SessionResponse,
  VideoSummary,
} from "@kidtube/shared";

const configuredBase = import.meta.env.VITE_API_BASE_URL?.trim() ?? "";
const API_BASE = configuredBase.replace(/\/$/, "");
let csrfToken = "";

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function request<T>(
  path: string,
  options: RequestInit & { mutation?: boolean } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("accept", "application/json");
  if (options.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (options.mutation === true && csrfToken.length > 0) {
    headers.set("x-csrf-token", csrfToken);
  }
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...options,
      headers,
      credentials: "include",
    });
  } catch {
    throw new ApiClientError(
      "NETWORK_ERROR",
      "Không thể kết nối KidTube Cloud. Kiểm tra mạng rồi thử lại.",
      0,
    );
  }
  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError("INVALID_RESPONSE", "Máy chủ trả về dữ liệu không hợp lệ.", response.status);
  }
  if (!response.ok || !payload.ok) {
    const error = payload.ok
      ? new ApiClientError("HTTP_ERROR", "Yêu cầu không thành công.", response.status)
      : new ApiClientError(payload.error.code, payload.error.message, response.status);
    if (error.status === 401) {
      csrfToken = "";
    }
    throw error;
  }
  return payload.data;
}

async function mutation<T>(
  path: string,
  method: string,
  body?: unknown,
  headers?: HeadersInit,
): Promise<T> {
  const refreshCsrf = async () => {
    const session = await request<SessionResponse>("/api/v1/auth/session");
    csrfToken = session.csrfToken;
  };
  const send = () => request<T>(path, {
    method,
    mutation: true,
    ...(headers === undefined ? {} : { headers }),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (csrfToken.length === 0) await refreshCsrf();
  try {
    return await send();
  } catch (error) {
    if (!(error instanceof ApiClientError) || error.code !== "CSRF_INVALID") throw error;
    await refreshCsrf();
    return send();
  }
}

export function idempotencyKey(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`;
}

export const api = {
  async session(): Promise<SessionResponse> {
    const session = await request<SessionResponse>("/api/v1/auth/session");
    csrfToken = session.csrfToken;
    return session;
  },

  async login(email: string, password: string): Promise<SessionResponse> {
    const session = await request<SessionResponse>("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      headers: { "content-type": "application/json" },
    });
    csrfToken = session.csrfToken;
    return session;
  },

  async logout(): Promise<void> {
    await mutation<{ loggedOut: boolean }>("/api/v1/auth/logout", "POST");
    csrfToken = "";
  },

  sessions: () => request<Array<Record<string, unknown>>>("/api/v1/auth/sessions"),
  revokeSession: (id: string) => mutation(`/api/v1/auth/sessions/${encodeURIComponent(id)}`, "DELETE"),

  devices: () => request<DeviceSummary[]>("/api/v1/parent/devices"),
  updateDevice: (id: string, update: { name?: string; dailyLimitMinutes?: number | null }) =>
    mutation(`/api/v1/parent/devices/${encodeURIComponent(id)}`, "PATCH", update),
  revokeDevice: (id: string) => mutation(`/api/v1/parent/devices/${encodeURIComponent(id)}`, "DELETE"),
  claimPairing: (code: string, name: string) =>
    mutation<{ deviceId: string }>("/api/v1/parent/pairing/claim", "POST", { code, name }),
  command: (
    deviceId: string,
    type: Exclude<DeviceCommandType, "BLOCK_AND_STOP">,
    payload: Record<string, unknown> = {},
  ) =>
    mutation<DeviceCommand>(
      `/api/v1/parent/devices/${encodeURIComponent(deviceId)}/commands`,
      "POST",
      { type, payload },
      { "idempotency-key": idempotencyKey(type.toLocaleLowerCase("en")) },
    ),
  blockAndStop: (deviceId: string, video: string) =>
    mutation<{ commandId: string }>(
      `/api/v1/parent/devices/${encodeURIComponent(deviceId)}/block-and-stop`,
      "POST",
      { video },
      { "idempotency-key": idempotencyKey("block-stop") },
    ),

  home: () => request<HomeSection[]>("/api/v1/parent/home"),
  createSection: (title: string) =>
    mutation<HomeSection>("/api/v1/parent/home/sections", "POST", { title }),
  updateSection: (id: string, update: { title?: string; enabled?: boolean; target?: HomeTarget }) =>
    mutation(`/api/v1/parent/home/sections/${encodeURIComponent(id)}`, "PATCH", update),
  deleteSection: (id: string) =>
    mutation(`/api/v1/parent/home/sections/${encodeURIComponent(id)}`, "DELETE"),
  reorderSections: (sectionIds: string[]) =>
    mutation("/api/v1/parent/home/sections/order", "PUT", { sectionIds }),
  addVideo: (sectionId: string, video: string) =>
    mutation<VideoSummary>(
      `/api/v1/parent/home/sections/${encodeURIComponent(sectionId)}/videos`,
      "POST",
      { video },
    ),
  addChannelVideos: (sectionId: string, channelId: string) =>
    mutation<{ channelId: string; scanned: number; added: number; skipped: number }>(
      `/api/v1/parent/home/sections/${encodeURIComponent(sectionId)}/channels`,
      "POST",
      { channelId },
    ),
  removeVideo: (sectionId: string, videoId: string) =>
    mutation(
      `/api/v1/parent/home/sections/${encodeURIComponent(sectionId)}/videos/${encodeURIComponent(videoId)}`,
      "DELETE",
    ),
  reorderVideos: (sectionId: string, videoIds: string[]) =>
    mutation(
      `/api/v1/parent/home/sections/${encodeURIComponent(sectionId)}/videos/order`,
      "PUT",
      { videoIds },
    ),

  youtubeSearch: (query: string, cursor?: string) => {
    const search = new URLSearchParams({ q: query });
    if (cursor !== undefined && cursor.length > 0) search.set("cursor", cursor);
    return request<{ items: ParentSearchResult[]; nextCursor: string | null }>(
      `/api/v1/parent/youtube/search?${search.toString()}`,
    );
  },

  blocks: () => request<BlockRules>("/api/v1/parent/blocks"),
  blockVideo: (video: string, reason = "") =>
    mutation("/api/v1/parent/blocks/videos", "POST", { video, reason }),
  blockChannel: (channelId: string, channelName = "", reason = "") =>
    mutation("/api/v1/parent/blocks/channels", "POST", { channelId, channelName, reason }),
  blockProgram: (value: string) =>
    mutation("/api/v1/parent/blocks/programs", "POST", { value }),
  blockKeyword: (value: string, matchType: "EXACT" | "CONTAINS") =>
    mutation("/api/v1/parent/blocks/keywords", "POST", { value, matchType }),
  removeBlock: (kind: BlockKind, id: string) =>
    mutation(`/api/v1/parent/blocks/${kind}/${encodeURIComponent(id)}`, "DELETE"),

  history: (days: 1 | 7 | 30) =>
    request<{ watch: HistoryWatch[]; search: HistorySearch[] }>(`/api/v1/parent/history?days=${days}`),
  schedules: (deviceId: string) =>
    request<Schedule[]>(`/api/v1/parent/devices/${encodeURIComponent(deviceId)}/schedules`),
  replaceSchedules: (deviceId: string, schedules: ScheduleInput[]) =>
    mutation(`/api/v1/parent/devices/${encodeURIComponent(deviceId)}/schedules`, "PUT", {
      schedules,
    }),
};

export type BlockKind = "videos" | "channels" | "programs" | "keywords";

export interface BlockRules {
  videos: Array<{
    youtube_video_id: string;
    title_snapshot: string | null;
    reason: string | null;
    created_at: string;
  }>;
  channels: Array<{
    youtube_channel_id: string;
    channel_name_snapshot: string | null;
    reason: string | null;
    created_at: string;
  }>;
  programs: Array<{ id: string; value: string; enabled: number; created_at: string }>;
  keywords: Array<{
    id: string;
    value: string;
    match_type: "EXACT" | "CONTAINS";
    enabled: number;
    created_at: string;
  }>;
}

export interface HistoryWatch {
  id: string;
  device_name: string;
  title_snapshot: string;
  channel_name_snapshot: string;
  started_at: string;
  ended_at: string | null;
  watch_seconds: number;
  completion_percent: number;
  parent_stopped: number;
}

export interface HistorySearch {
  id: string;
  device_name: string;
  query: string;
  searched_at: string;
  result_count: number;
}

export interface Schedule {
  id: string;
  day_of_week: number;
  start_minute: number;
  end_minute: number;
  enabled: number;
}

export interface ScheduleInput {
  dayOfWeek: number;
  startMinute: number;
  endMinute: number;
  enabled: boolean;
}
